/**
 * Keeps the cart in step with the pickup store the shopper chose, so checkout opens on it
 * and can explain itself when it can't.
 *
 * Two things are synced: the cart's preferred pickup location (what checkout selects), and
 * cart attributes naming the store and any items it can't fulfill (what the checkout
 * message reads). Both follow the same moments — the selection changes, the cart changes,
 * and page load — and share one `/cart.js` fetch. An empty cart is skipped, since there is
 * nothing to collect.
 *
 * Fails open throughout: nothing here blocks the UI or shows an error, because a missed sync
 * only means checkout isn't preselected and says nothing, which is how it behaved before.
 */

import { ThemeEvents } from '@theme/events';
import { findItemsMissingFromStore } from '@theme/pickup-store-cart-availability';
import {
  buildPickupAttributes,
  hasPickupAttributes,
  writePickupAttributes,
} from '@theme/pickup-store-cart-attributes';
import {
  clearPickupDeliveryPreference,
  setPickupDeliveryPreference,
} from '@theme/pickup-store-delivery-preference';
import { PICKUP_STORE_CHANGED_EVENT, readSelectedStore } from '@theme/pickup-store-storage';
import { readStorefrontToken } from '@theme/storefront-api';

/** The last cart, store and items written, so a cart update that changed none costs no request. */
let lastSyncedKey = '';

/** Syncs run one after another: two in flight would each read a cart the other is changing. */
let queue = Promise.resolve();

/** @returns {Promise<any | null>} */
async function fetchCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });

    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

/**
 * Takes our store off a cart whose shopper no longer has one we can name, so checkout isn't
 * left preselecting — or apologising for — a store they've moved on from.
 * @param {any} cart
 */
async function clearPickupStore(cart) {
  if (!hasPickupAttributes(cart)) return;

  const updated = await writePickupAttributes(cart, buildPickupAttributes(null, []));
  if (updated) await clearPickupDeliveryPreference(updated.token);

  lastSyncedKey = '';
}

/**
 * @param {any} cart
 * @param {import('@theme/pickup-store-storage').SelectedStore & { id: string }} store
 */
async function syncPickupStore(cart, store) {
  const items = cart.items.map((/** @type {{ variant_id: number }} */ item) => item.variant_id);
  const unavailableItems = await findItemsMissingFromStore(cart.token, store.id);

  // Attributes first: the write can hand back a new cart token, which the preference needs.
  const updated = await writePickupAttributes(cart, buildPickupAttributes(store, unavailableItems));
  if (!updated) return;

  if (await setPickupDeliveryPreference(updated.token, store)) {
    lastSyncedKey = `${updated.token}:${store.id}:${items.join(',')}`;
  }
}

async function syncPickup() {
  const cart = await fetchCart();
  if (!cart || cart.item_count === 0) return;

  const store = readSelectedStore(window.Shopify?.country);
  if (!store?.id) {
    await clearPickupStore(cart);
    return;
  }

  const items = cart.items.map((/** @type {{ variant_id: number }} */ item) => item.variant_id);
  if (`${cart.token}:${store.id}:${items.join(',')}` === lastSyncedKey) return;

  await syncPickupStore(cart, { ...store, id: store.id });
}

function enqueueSync() {
  queue = queue.then(syncPickup).catch(() => {});
}

// Without a token every sync would fetch /cart.js only to fail, so don't listen at all.
if (readStorefrontToken()) {
  document.addEventListener(PICKUP_STORE_CHANGED_EVENT, enqueueSync);
  document.addEventListener(ThemeEvents.cartUpdate, enqueueSync);
  enqueueSync();
}
